const STATUS_STYLE = {
  complete: {
    label: 'Complete',
    color: '#16A34A',
    background: '#DCFCE7',
    border: '#BBF7D0'
  },
  cancelled: {
    label: 'Cancelled',
    color: '#64748B',
    background: '#F1F5F9',
    border: '#CBD5E1'
  },
  noData: {
    label: 'No Data',
    color: '#64748B',
    background: '#F1F5F9',
    border: '#CBD5E1'
  },
  overdue: {
    label: 'Overdue',
    color: '#DC2626',
    background: '#FEE2E2',
    border: '#FECACA'
  },
  onTrack: {
    label: 'On Track',
    color: '#16A34A',
    background: '#DCFCE7',
    border: '#BBF7D0'
  },
  needsAttention: {
    label: 'Needs Attention',
    color: '#F59E0B',
    background: '#FEF3C7',
    border: '#FDE68A'
  }
}

export function isCompleteStatus(status) {
  return status === 'delivered' || status === 'approved'
}

export function getCompletedDeliveryCount(deliveries = []) {
  return deliveries.filter(delivery => isCompleteStatus(delivery.status)).length
}

export function getDeliveryProgress(deliveries = [], item = {}) {
  if (item.status === 'cancelled') return 0
  if (item.status === 'completed') return 100
  if (!deliveries.length) return 0

  return Math.round((getCompletedDeliveryCount(deliveries) / deliveries.length) * 100)
}

export function getProgressLabel(score) {
  if (score === 100) return 'Complete'
  return 'Progress'
}

export function normaliseDate(value) {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  date.setHours(0, 0, 0, 0)
  return date
}

export function daysUntil(date) {
  const target = normaliseDate(date)

  if (!target) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return Math.ceil((target - today) / (1000 * 60 * 60 * 24))
}

export function getDeliveryDueDate(delivery) {
  const directDate = normaliseDate(delivery.delivery_date)

  if (directDate) return directDate

  const customFields = delivery.custom_fields || {}

  const preferredKeys = [
    'due_date',
    'delivery_due_date',
    'delivery_date',
    'date_due',
    'activation_date',
    'date_of_activation',
    'publish_date',
    'post_date',
    'deadline',
    'date'
  ]

  for (const key of preferredKeys) {
    const customDate = normaliseDate(customFields[key])

    if (customDate) return customDate
  }

  const fallbackKey = Object.keys(customFields).find(key => {
    const normalised = key.toLowerCase()

    return (
      normalised.includes('due') ||
      normalised.includes('deadline') ||
      normalised.includes('activation') ||
      normalised.includes('scheduled') ||
      normalised.includes('date')
    )
  })

  return fallbackKey ? normaliseDate(customFields[fallbackKey]) : null
}

export function getTimelineStatus(item = {}, deliveries = [], options = {}) {
  const {
    behindThreshold = 15,
    attentionWindowDays = 30,
    attentionWindowMinimumProgress = 80,
    checkDeliveryDueDates = true
  } = options

  const progress = getDeliveryProgress(deliveries, item)

  if (item.status === 'cancelled') return STATUS_STYLE.cancelled
  if (progress >= 100 || item.status === 'completed') return STATUS_STYLE.complete
  if (!deliveries.length) return STATUS_STYLE.noData

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (checkDeliveryDueDates) {
    const hasOverdueDeliveries = deliveries.some(delivery => {
      if (isCompleteStatus(delivery.status)) return false

      const dueDate = getDeliveryDueDate(delivery)

      return dueDate && dueDate < today
    })

    if (hasOverdueDeliveries) return STATUS_STYLE.overdue
  }

  const startDate = normaliseDate(item.start_date)
  const endDate = normaliseDate(item.end_date)
  const daysToEnd = daysUntil(item.end_date)

  if (endDate && endDate < today) return STATUS_STYLE.overdue
  if (!startDate || !endDate) return STATUS_STYLE.onTrack
  if (today < startDate) return STATUS_STYLE.onTrack

  const totalDuration = endDate - startDate
  const elapsed = today - startDate

  if (totalDuration <= 0) return STATUS_STYLE.needsAttention

  const expectedProgress = Math.min(Math.max(Math.round((elapsed / totalDuration) * 100), 0), 100)
  const behindBy = expectedProgress - progress

  if (
    daysToEnd !== null &&
    daysToEnd <= attentionWindowDays &&
    progress < attentionWindowMinimumProgress
  ) {
    return STATUS_STYLE.needsAttention
  }

  if (behindBy > behindThreshold) return STATUS_STYLE.needsAttention

  return STATUS_STYLE.onTrack
}
